import { Web3DViewerWrapper } from '@trimble/utilities';
import { CapabilityBase } from '@trimble/web3d-capabilities';
import { CapabilityProviderBase } from '@trimble/web3d-providers';
import { Accessor, createMemo, createSignal } from 'solid-js';
import { CapabilityRegistryBase } from './capability-registry-base';
import {
  CapabilityBaseT,
  Constructor,
  ParentResourceCollections,
  ResourceCollections,
} from './capability-registry-types';

/**
 * Interface defining what CapabilityRegistryContainer needs from its parent registry.
 * This avoids circular dependency with CapabilityRegistry.
 */
export interface IParentRegistry extends ParentResourceCollections {
  get<T extends CapabilityBaseT>(
    classRef: Constructor<T>
  ): Accessor<T | undefined>;
  cleanup(): Promise<void>;
}

/**
 * Container for a pane-scoped capability-registry with auto-registered viewer and plugins.
 * Provides convenient access to pane-specific resources.
 *
 * Primary use case: Pass to pane-scoped coordinators so they can access pane resources
 * while maintaining clean separation from other panes.
 */
export class CapabilityRegistryContainer
  extends CapabilityRegistryBase
  implements ResourceCollections
{
  private readonly parentRegistry: IParentRegistry;
  private readonly setViewerWrapperSignal: (
    v: Web3DViewerWrapper | undefined
  ) => void;
  public readonly viewerWrapper: Accessor<Web3DViewerWrapper | undefined>;

  public readonly capabilities: ResourceCollections['capabilities'];
  public readonly providers: ResourceCollections['providers'];
  public readonly coordinators: ResourceCollections['coordinators'];
  public readonly capabilitiesWithParent: ResourceCollections['capabilities'];
  public readonly providersWithParent: ResourceCollections['providers'];
  public readonly coordinatorsWithParent: ResourceCollections['coordinators'];

  public paneRowIdx?: number;
  public paneCellIdx?: number;

  constructor(parent: IParentRegistry, viewerWrapper?: Web3DViewerWrapper) {
    super();
    this.parentRegistry = parent;
    const [getViewerWrapper, setViewerWrapper] = createSignal(viewerWrapper);
    this.viewerWrapper = getViewerWrapper;
    this.setViewerWrapperSignal = setViewerWrapper;

    this.capabilities = this.collectCapabilities();
    this.providers = this.collectProviders();
    this.coordinators = this.collectCoordinators();

    this.capabilitiesWithParent = createMemo<CapabilityBase[]>(() => {
      return [...this.capabilities(), ...this.parentRegistry.capabilities()];
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.providersWithParent = createMemo<CapabilityProviderBase<any>[]>(() => {
      return [...this.providers(), ...this.parentRegistry.providers()];
    });

    this.coordinatorsWithParent = createMemo(() => {
      return [...this.coordinators(), ...this.parentRegistry.coordinators()];
    });

    if (viewerWrapper) {
      this.setViewerWrapper(viewerWrapper);
    }
  }

  /**
   * Set or update the viewer wrapper and register all plugins.
   * Can be called after construction if viewer wasn't available initially.
   * Also adds the viewer to all shared providers in the parent capability-registry.
   */
  public setViewerWrapper(viewerWrapper: Web3DViewerWrapper): void {
    this.setViewerWrapperSignal(viewerWrapper);

    const sharedProviders = this.parentRegistry.sharedProviders();
    sharedProviders.forEach((provider) => {
      provider.addViewer(viewerWrapper);
    });
  }

  // Note: we allow retrieving capabilities from the parent registry as well.
  public override get<T extends CapabilityBaseT>(
    classRef: Constructor<T>
  ): Accessor<T | undefined> {
    const currentRegistry = this.getRegistry();
    const localEntry = currentRegistry.get(
      classRef as Constructor<CapabilityBaseT>
    );

    if (localEntry) {
      return localEntry[0] as Accessor<T | undefined>;
    }

    return this.parentRegistry.get(classRef);
  }

  public async cleanup(): Promise<void> {
    await Promise.all(
      [...this.capabilities(), ...this.providers(), ...this.coordinators()].map(
        (cap) => cap.cleanup()
      )
    );
  }
}
